import { useEffect, useMemo, useRef, useState } from 'react';
import { BiPlus, BiSearch } from 'react-icons/bi';
import { FaClockRotateLeft, FaUserGroup } from 'react-icons/fa6';
import { GoPeople } from 'react-icons/go';
import { HiMiniWrenchScrewdriver } from 'react-icons/hi2';
import { IoCreateOutline, IoDocumentTextOutline } from 'react-icons/io5';
import { LuWaves } from 'react-icons/lu';
import { MdOutlineCalendarMonth, MdOutlinePower } from 'react-icons/md';
import { useSearchParams } from 'react-router-dom';

import { getServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { DateNavPicker } from '@/components/DateNavPicker';
import { Input } from '@/components/Inputs';
import { Tile } from '@/components/Tile/Tile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { mapPeopleForSelection } from '@/store/persons/person.mapper';
import { usePerson } from '@/store/persons/person.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { isOwnerGroupRole, UserRole } from '@/utils/roles';
import { getBrowserTimeZone } from '@/utils/timezone';
import { toast } from '@/utils/toast';

import { LOG_VIEW_PARAM, useLogView } from '../useLogView';

import { CrewRosterSection } from './components/CrewRosterSection';
import { DailyHeaderFields } from './components/DailyHeaderFields';
import { DayCountsSection } from './components/DayCountsSection';
import { DayWorkersSection } from './components/DayWorkersSection';
import { EntryListSection } from './components/EntryListSection';
import { MainFields } from './components/MainFields';
import { ShoreConnectionsSection } from './components/ShoreConnectionsSection';
import { SignaturesSection } from './components/SignaturesSection';
import { TankLevelsSection } from './components/TankLevelsSection';
import s from './DailyLog.module.scss';
import DailyLogHistory from './DailyLogHistory/DailyLogHistory';
import { hasDayWorkerErrors } from './dayWorkerValidation';
import { useDailyLog } from './hooks/useDailyLog';
import { syncDailyLogCrewTasks } from './hooks/useDailyLogCrewTasks';
import { useGeolocation } from './hooks/useGeolocation';
import { crewMemberToRosterEntry, useLogCrew } from './hooks/useLogCrew';
import { dailyLogToFormState, formStateToDailyLog } from './mappers';

const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

const isoToParam = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const paramToISO = (param: string | null): string => {
  if (param && DATE_PARAM_RE.test(param)) {
    const d = new Date(`${param}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
};

const DailyLog = () => {
  const { people, getPeople, hasFetchedPeople, currentUser } = usePerson();
  const vessel = useVessels((s) => s.vessel);
  const createDailyLog = useVessels((s) => s.createDailyLog);
  const editDailyLog = useVessels((s) => s.editDailyLog);
  const getDailyLogByDate = useVessels((s) => s.getDailyLogByDate);
  const getDailyLogPdf = useVessels((s) => s.getDailyLogPdf);
  const signDailyLog = useVessels((s) => s.signDailyLog);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { view, openHistory, openedFromLogsCard, startNewEntry } = useLogView();
  const [historySearch, setHistorySearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const {
    formState,
    addedCrewIds,
    setField,
    addCrewMember,
    removeCrewMember,
    updateCrewEntry,
    assignOfficer,
    addNarrativeEntry,
    removeNarrativeEntry,
    updateNarrativeEntry,
    addDrillEntry,
    removeDrillEntry,
    updateDrillEntry,
    setShoreConnection,
    setTankLevel,
    addDayWorker,
    removeDayWorker,
    updateDayWorker,
    signCaptain,
    prefillForm,
    resetForm,
    signOfficer,
  } = useDailyLog();
  const isMobile = useIsMobile();
  const { volumeUnit } = useUnitPreferences();

  const [fetchedLog, setFetchedLog] = useState<
    Awaited<ReturnType<typeof getDailyLogByDate>> | undefined
  >(undefined);
  const [hasResolvedFetch, setHasResolvedFetch] = useState(false);

  const { geoPlace, countryCode, issue: geoIssue, locating: geoLocating } = useGeolocation();
  const { crew, crewById, crewForPicker, nonCrewIds } = useLogCrew();
  const prefilledCountryRef = useRef(false);
  const prefilledLocationRef = useRef(false);
  const prefilledCrewRef = useRef(false);

  useEffect(() => {
    const iso = paramToISO(dateParam);
    setHasResolvedFetch(false);
    prefilledCountryRef.current = false;
    prefilledLocationRef.current = false;
    prefilledCrewRef.current = false;
    void (async () => {
      const log = await getDailyLogByDate(iso);
      setFetchedLog(log ?? undefined);
      if (!log) {
        resetForm(iso);
      }
      setHasResolvedFetch(true);
    })();
  }, [dateParam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasFetchedPeople) {
      void getPeople();
    }
  }, [hasFetchedPeople, getPeople]);

  useEffect(() => {
    if (hasFetchedPeople && fetchedLog) {
      const state = dailyLogToFormState(
        fetchedLog,
        mapPeopleForSelection(people ?? []),
        volumeUnit,
      );
      prefillForm({
        ...state,
        // The roster is the crew. An entry saved while the picker still offered
        // everyone but guests can name the owner or a vendor, so they come off
        // it here rather than being drawn as crew for the day.
        crewRoster: state.crewRoster.filter((entry) => !nonCrewIds.has(entry.userId)),
      });
    }
  }, [hasFetchedPeople, fetchedLog]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * **A new day starts with the crew already on it.**
   *
   * Only for an entry that has never been saved: once a log exists, its own
   * crew list is the record — someone taken off yesterday's entry stays off it,
   * and someone who has since joined the vessel does not appear on a day they
   * were not aboard. The ref is cleared per date, so this fires once per new
   * entry and a name removed before saving is not put straight back.
   */
  useEffect(() => {
    if (!hasResolvedFetch || fetchedLog || prefilledCrewRef.current) return;
    if (!crew.length) return;
    prefilledCrewRef.current = true;
    setField('crewRoster', crew.map(crewMemberToRosterEntry));
  }, [hasResolvedFetch, fetchedLog, crew, setField]);

  useEffect(() => {
    if (
      hasResolvedFetch &&
      !fetchedLog &&
      countryCode &&
      !formState.country &&
      !prefilledCountryRef.current
    ) {
      prefilledCountryRef.current = true;
      setField('country', countryCode);
    }
  }, [hasResolvedFetch, fetchedLog, countryCode, formState.country, setField]);

  useEffect(() => {
    if (
      hasResolvedFetch &&
      !fetchedLog &&
      geoPlace?.formattedAddress &&
      !formState.location &&
      !prefilledLocationRef.current
    ) {
      prefilledLocationRef.current = true;
      setField('location', geoPlace.formattedAddress);
    }
  }, [hasResolvedFetch, fetchedLog, geoPlace, formState.location, setField]);

  const allUsers = useMemo(
    () => mapPeopleForSelection(people ?? []).filter((u) => u.role !== UserRole.GUEST),
    [people],
  );

  const { can } = usePermissions();
  const currentUserCanSignAsCaptain = can(PERMISSIONS.logbook.DailyLogSignCaptain);
  const isCurrentUserOwner = isOwnerGroupRole(
    currentUser?.services?.find((s) => s.service_id === getServiceId())?.role,
  );

  const captainEligibleUsers = useMemo(() => {
    const currentServiceId = getServiceId();
    const eligiblePeople = (people ?? []).filter((p) =>
      (p.services ?? []).some(
        (r) =>
          r.service_id === currentServiceId &&
          (r.rights ?? []).includes(PERMISSIONS.logbook.DailyLogSignCaptain),
      ),
    );
    return mapPeopleForSelection(eligiblePeople);
  }, [people]);

  const captainUser = useMemo(() => {
    const assignedId = formState.captainSignature.personId;
    if (assignedId) {
      return allUsers.find((u) => u.id === assignedId) ?? null;
    }
    if (currentUser && currentUserCanSignAsCaptain) {
      return {
        id: currentUser.id,
        firstName: currentUser.person_info.first_name,
        lastName: currentUser.person_info.last_name,
        avatar: imageVariantLink(currentUser.avatar, 40) ?? '',
        role: UserRole.CAPTAIN,
      };
    }
    if (isCurrentUserOwner) {
      return captainEligibleUsers[0] ?? null;
    }
    return null;
  }, [
    allUsers,
    formState.captainSignature.personId,
    currentUser,
    currentUserCanSignAsCaptain,
    isCurrentUserOwner,
    captainEligibleUsers,
  ]);

  const crewEligibleUsers = crewForPicker;

  const officerUser = useMemo(
    () => allUsers.find((u) => u.id === formState.officerSignature.personId) ?? null,
    [allUsers, formState.officerSignature.personId],
  );

  const navigateToDate = (iso: string) => {
    const param = isoToParam(iso);
    const today = isoToParam(new Date().toISOString());
    setSearchParams(
      (prev) => {
        if (!param || param === today) prev.delete('date');
        else prev.set('date', param);
        // A picked day is always shown as its page, and this has to be one
        // navigation: a second `setSearchParams` in the same tick would start
        // from the old params and drop the date again.
        prev.delete(LOG_VIEW_PARAM);
        return prev;
      },
      { replace: true },
    );
  };

  const handleDateChange = (date: string) => navigateToDate(date);

  const persistLog = async (): Promise<string | undefined> => {
    const previousSignerId = fetchedLog?.signatures?.first_officer_id ?? null;
    const nextSignerId = formState.officerSignature.personId ?? null;

    const previousCrewIds = Array.from(
      new Set(
        [...(fetchedLog?.crew ?? []).map((e) => e.crew_id), previousSignerId].filter(
          (id): id is string => !!id,
        ),
      ),
    );
    const nextCrewIds = Array.from(
      new Set(
        [...formState.crewRoster.map((e) => e.userId), nextSignerId].filter(
          (id): id is string => !!id,
        ),
      ),
    );
    const log = formStateToDailyLog(formState, volumeUnit);
    log.signatures.captain_id = log.signatures.captain_id ?? captainUser?.id ?? null;
    log.signatures.first_officer_id =
      log.signatures.first_officer_id ?? formState.officerSignature.personId ?? null;
    let dailyLogId: string | undefined;
    if (fetchedLog?.id) {
      await editDailyLog(log, fetchedLog.id);
      dailyLogId = fetchedLog.id;
    } else {
      const created = await createDailyLog(log);
      dailyLogId = created?.id;
    }
    if (dailyLogId) {
      await syncDailyLogCrewTasks(
        dailyLogId,
        log.header.date,
        previousCrewIds,
        nextCrewIds,
        allUsers,
      );
    }
    return dailyLogId;
  };

  const handleSignCaptain = async () => {
    if (!captainUser) return;
    signCaptain(captainUser.id);
    setIsSaving(true);
    try {
      const id = await persistLog();
      if (!id) return;
      await signDailyLog(id);
      const refreshed = await getDailyLogByDate(formState.date);
      setFetchedLog(refreshed);
    } finally {
      setIsSaving(false);
    }
  };

  const isLocked = !!fetchedLog?.signatures?.captain_signed_at;
  const isOfficerLocked = !!fetchedLog?.signatures?.first_officer_signed_at;
  const canAssignOfficer = (!!captainUser || isCurrentUserOwner) && !isOfficerLocked;
  const canSignOfficerAsCurrentUser =
    !!officerUser && !!currentUser && currentUser.id === officerUser.id;

  const handleAssignOfficer = (personId: string) => assignOfficer(personId);
  const handleSignOfficer = async () => {
    if (!officerUser || !canSignOfficerAsCurrentUser) return;
    signOfficer(officerUser.id);
    setIsSaving(true);
    try {
      const id = await persistLog();
      if (!id) return;
      await signDailyLog(id);
      const refreshed = await getDailyLogByDate(formState.date);
      setFetchedLog(refreshed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async () => {
    if (!fetchedLog?.id) return;
    setIsExporting(true);
    try {
      const blob = await getDailyLogPdf(fetchedLog.id, getBrowserTimeZone());
      await downloadBlob(blob, `daily-log-${formState.date ?? 'export'}.pdf`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSave = async () => {
    // The day-worker rows are the only fields the page validates, and they are
    // far enough down that the inline errors can be off screen — so say what is
    // wrong rather than letting the button do nothing.
    if (hasDayWorkerErrors(formState.dayWorkers)) {
      toast.error('Couldn’t save. Please check the day workers’ details.');
      return;
    }

    setIsSaving(true);
    try {
      await persistLog();
      const refreshed = await getDailyLogByDate(formState.date);
      setFetchedLog(refreshed);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div
        className={s.toolbar}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginBlock: 16,
          width: '100%',
          position: 'sticky',
          top: isMobile ? 60 : 10,
          zIndex: 9,
        }}
      >
        <div className={s.toolbarControls}>
          {view === 'form' && (
            <ButtonOutlinedSmall onClick={openHistory}>
              <FaClockRotateLeft />
              History
            </ButtonOutlinedSmall>
          )}
          {!isMobile && view === 'form' && (
            <DateNavPicker
              value={formState.date}
              onChange={handleDateChange}
              maxDate={new Date()}
            />
          )}
          {view === 'form' && (
            <div className={s.toolbarActions} style={{ display: 'flex', gap: 12 }}>
              <ButtonOutlinedSmall
                onClick={() => void handleExport()}
                disabled={isExporting || !fetchedLog?.id}
              >
                {isExporting ? 'Exporting…' : 'Export'}
              </ButtonOutlinedSmall>
              <ButtonSmall onClick={() => void handleSave()} disabled={isSaving || isLocked}>
                {isSaving ? 'Saving…' : 'Save'}
              </ButtonSmall>
            </div>
          )}
        </div>
        {view === 'history' && (
          <Input
            placeholder="Search"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            startIcon={<BiSearch size={18} />}
            className={s.searchInput}
          />
        )}
        {openedFromLogsCard && (
          <ButtonSmall onClick={startNewEntry} aria-label="Add Entry">
            <BiPlus size={20} aria-hidden="true" />
          </ButtonSmall>
        )}
      </div>
      {isMobile && view === 'form' && (
        <DateNavPicker
          value={formState.date}
          onChange={handleDateChange}
          maxDate={new Date()}
          className={s.mobileDatePicker}
        />
      )}
      {view === 'history' && (
        <DailyLogHistory search={historySearch} onSelectLog={navigateToDate} />
      )}
      {view === 'form' && (
        <div className={s.content}>
          <div className={s.sectionsList}>
            <Tile>
              <DailyHeaderFields
                shipName={vessel?.vessel_name ?? ''}
                registrationNumber={vessel?.registration_number ?? ''}
                onImoNumber={vessel?.structure_number_value ?? ''}
              />
            </Tile>

            <Tile>
              <MainFields
                location={formState.location}
                country={formState.country}
                geoPlace={geoPlace}
                geoIssue={hasResolvedFetch && !fetchedLog ? geoIssue : undefined}
                geoLocating={hasResolvedFetch && !fetchedLog && geoLocating}
                date={formState.date}
                sternTo={formState.sternTo}
                alongside={formState.alongside}
                anchored={formState.anchored}
                disabled={isLocked}
                onChange={(field, val) => {
                  if (field === 'date') {
                    void handleDateChange(val);
                  } else {
                    setField(field, val);
                  }
                }}
                onMooringChange={(field, val) => setField(field, val)}
                watchkeeperIds={formState.watchkeeperIds}
                allUsers={allUsers}
                onAddWatchkeeper={(userId) =>
                  setField('watchkeeperIds', [...formState.watchkeeperIds, userId])
                }
                onRemoveWatchkeeper={(userId) =>
                  setField(
                    'watchkeeperIds',
                    formState.watchkeeperIds.filter((id) => id !== userId),
                  )
                }
              />
            </Tile>

            <fieldset disabled={isLocked} style={{ display: 'contents' }}>
              <div className={s.sectionsList} style={isLocked ? { opacity: 0.7 } : undefined}>
                <Tile>
                  <CrewRosterSection
                    crewRoster={formState.crewRoster}
                    allUsers={crewForPicker}
                    addedIds={addedCrewIds}
                    crewById={crewById}
                    disabled={isLocked}
                    onAdd={addCrewMember}
                    onUpdate={updateCrewEntry}
                    onRemove={removeCrewMember}
                    icon={FaUserGroup}
                  />
                </Tile>

                <Tile>
                  <EntryListSection
                    icon={IoDocumentTextOutline}
                    title="Narrative"
                    addLabel="Add Narrative"
                    entries={formState.narrativeEntries}
                    onAdd={addNarrativeEntry}
                    onUpdate={updateNarrativeEntry}
                    onRemove={removeNarrativeEntry}
                  />
                </Tile>

                <Tile>
                  <EntryListSection
                    icon={HiMiniWrenchScrewdriver}
                    title="Drills"
                    addLabel="Add Drill"
                    entries={formState.drillEntries}
                    onAdd={addDrillEntry}
                    onUpdate={updateDrillEntry}
                    onRemove={removeDrillEntry}
                  />
                </Tile>

                <div className={s.twoColRow}>
                  <Tile>
                    <ShoreConnectionsSection
                      values={formState.shoreConnections}
                      onChange={setShoreConnection}
                      icon={MdOutlinePower}
                    />
                  </Tile>

                  <Tile>
                    <TankLevelsSection
                      values={formState.tankLevels}
                      onChange={setTankLevel}
                      icon={LuWaves}
                    />
                  </Tile>
                </div>

                <Tile>
                  <DayCountsSection
                    usageDays={formState.usageDays}
                    underwayDays={formState.underwayDays}
                    ownerGuestMeals={formState.ownerGuestMeals}
                    onChange={(field, val) => setField(field, val)}
                    icon={MdOutlineCalendarMonth}
                  />
                </Tile>

                <Tile>
                  <DayWorkersSection
                    workers={formState.dayWorkers}
                    onAdd={addDayWorker}
                    onUpdate={updateDayWorker}
                    onRemove={removeDayWorker}
                    icon={GoPeople}
                  />
                </Tile>

                <Tile>
                  <SignaturesSection
                    captain={formState.captainSignature}
                    officer={formState.officerSignature}
                    captainUser={captainUser}
                    officerUser={officerUser}
                    officerEligibleUsers={crewEligibleUsers}
                    canAssignOfficer={canAssignOfficer}
                    isOfficerLocked={isOfficerLocked}
                    canSignOfficerAsCurrentUser={canSignOfficerAsCurrentUser}
                    onAssignOfficer={handleAssignOfficer}
                    onSignCaptain={() => void handleSignCaptain()}
                    onSignOfficer={() => void handleSignOfficer()}
                    icon={IoCreateOutline}
                  />
                </Tile>
              </div>
            </fieldset>
          </div>
        </div>
      )}
    </>
  );
};

export default DailyLog;
